// What the words on a v4 result mean: Taylor's healthy and weaponized form of each of the nine
// gifts and each gift through each domain (Oct 21, 2026, verbatim from
// docs/source/taylor-gift-descriptions-2026-10-21.md), and the explainers for the quadrants,
// domains, freedom themes, blind spot and office pattern (Taylor's words where he wrote them,
// the rest drafted from them and listed in docs/COPY-REVIEW.md).
//
// One copy for everything that explains a result: the results page (apps/web) and the MCP's
// get_framework tool, so an AI app connected to Gifts Profile explains it the same way.

import type { DomainCode, QuadrantName } from "./types.js";

export interface GiftExplainer {
  /** The free form. */
  healthy: string;
  /** When freedom is low. */
  weaponized: string;
  /** What it looks like through each domain. */
  byDomain: Record<DomainCode, string>;
}

export const GIFT_EXPLAINERS: Record<string, GiftExplainer> = {
  Prophecy: {
    healthy: "You sense what's really going on and name the truth that others feel but won't say.",
    weaponized: "You either swallow the truth to stay accepted or deliver it so harshly that it wounds more than it helps.",
    byDomain: { STR: "Names the flaw in the plan", REL: "Tells a friend the truth privately", EXE: "Calls out what isn't getting done", INF: "Says it to the whole room" },
  },
  Serving: {
    healthy: "You notice practical needs before anyone asks and quietly meet them.",
    weaponized: "You serve to earn your worth, quietly resent being taken for granted, and can't let anyone serve you back.",
    byDomain: { STR: "Anticipates needs before they exist", REL: "Serves through presence and care", EXE: "Handles it before anyone asks", INF: "Rallies others to serve" },
  },
  Teaching: {
    healthy: "You turn confusing ideas into clear understanding that others can learn from and use.",
    weaponized: "You need to be right, so your knowledge becomes armor and correcting others becomes a way to feel safe.",
    byDomain: { STR: "Builds frameworks and systems", REL: "Teaches one person over coffee", EXE: "Shows how, step by step", INF: "Teaches from a stage" },
  },
  Exhorting: {
    healthy: "You see potential in people and give them the courage to take their next step.",
    weaponized: "You push others forward with hype and pep talks while avoiding your own stuff.",
    byDomain: { STR: "Shows people the path forward", REL: "Believes in someone one-on-one", EXE: "Pushes them to the next action", INF: "Fires up a crowd" },
  },
  Giving: {
    healthy: "You release your money, time and access freely to meet needs, without keeping score.",
    weaponized: "You give to buy love, access or control, or you swing the other way and hold everything tight out of fear there won't be enough.",
    byDomain: { STR: "Invests where it multiplies", REL: "Gives to the people they love", EXE: "Meets the practical need fast", INF: "Inspires others to give" },
  },
  Leading: {
    healthy: "You organize people and plans so the work runs well and everyone can flourish.",
    weaponized: "You control and micromanage because order is the only place you feel safe.",
    byDomain: { STR: "Designs the system", REL: "Leads through trust", EXE: "Runs the operation", INF: "Leads through vision and momentum" },
  },
  Mercy: {
    healthy: "You sit with people in their pain without rushing to fix it.",
    weaponized: "You absorb everyone's pain with no boundaries, rescuing people until you've lost yourself.",
    byDomain: { STR: "Understands why someone hurts", REL: "Sits with them in it", EXE: "Brings the meal", INF: "Rallies support around them" },
  },
  Pioneering: {
    healthy: "You see what doesn't exist yet and get it off the ground.",
    weaponized: "You start new things to prove you matter, then leave before they're finished or cling so tightly no one else can run them.",
    byDomain: { STR: "Architects the new thing", REL: "Starts it with a few close people", EXE: "Builds the first version by hand", INF: "Launches it and gets people in" },
  },
  Outreach: {
    healthy: "You naturally draw new people toward what you believe and care about.",
    weaponized: "You perform for the crowd and measure your worth by how many people respond.",
    byDomain: { STR: "Wins people with reasons", REL: "Brings people in through friendship", EXE: "Brings people in by serving them", INF: "Brings people in through energy and invitation" },
  },
};


/** The four quadrants. "Energy with no target" and "a plan you can't step into" are Taylor's. */
export const QUADRANT_TIPS: Record<QuadrantName, string> = {
  // Drafted from his definitions of Freedom and Clarity.
  Activated: "High freedom and high clarity: you know how you're built, and your past and fears aren't steering your decisions.",
  Drifting: "High freedom, low clarity: energy with no target.",
  Caged: "High clarity, low freedom: a plan you can't step into.",
  // Drafted from his definitions of Freedom and Clarity.
  Fragmented: "Low freedom and low clarity: old fears are still steering, and the direction isn't clear yet.",
};

/**
 * Each domain in Taylor's words (v4.1 doc, "Domains"): what they do first, how it shows up free,
 * and what it turns into under pressure. One meaning per domain, so the tooltips differ (Oct 22:
 * the shared "default way of engaging the world" line repeated on all four).
 */
export const DOMAIN_MEANINGS: Record<DomainCode, { name: string; first: string; free: string; pressure: string }> = {
  STR: { name: "Strategic", first: "step back and think it through first", free: "vision and wisdom", pressure: "overthinking" },
  REL: { name: "Relating", first: "connect first", free: "deep presence and real connection", pressure: "people-pleasing" },
  EXE: { name: "Executing", first: "do something first", free: "faithful service from rest", pressure: "overworking" },
  INF: { name: "Influencing", first: "move others first", free: "calling people higher", pressure: "overpowering" },
};

/** Everything after the domain's name: "people step back and think it through first. Free, …" */
export const domainMeaningAfterName = (code: DomainCode): string => {
  const m = DOMAIN_MEANINGS[code];
  return `people ${m.first}. Free, that's ${m.free}. Under pressure, it can turn into ${m.pressure}.`;
};

/** "Strategic people step back and think it through first. Free, that's vision and wisdom. Under pressure, it can turn into overthinking." */
export const domainTip = (code: DomainCode): string => `${DOMAIN_MEANINGS[code].name} ${domainMeaningAfterName(code)}`;

/** Taylor's: the domain picked least. */
export const BLIND_SPOT_TIP =
  "Your blind spot is the domain you picked least. It's usually the one you skip under pressure, and that's often where your gifts stall out.";

/** The four freedom themes, Taylor's names and meanings, in his order. */
export const FREEDOM_THEMES: { code: string; name: string; meaning: string }[] = [
  { code: "PAST", name: "The past", meaning: "The past is still steering. Old events, old timelines and the sense that it’s too late are setting your pace." },
  { code: "PERM", name: "Permission", meaning: "Somewhere you agreed that wanting more is wrong or costly, so you hold back or undo your own progress." },
  { code: "ARM", name: "Armor", meaning: "You’re protecting yourself by performing: holding back, over-preparing and showing the acceptable version of you." },
  { code: "EXP", name: "Expectation", meaning: "You expect good things not to last, so you settle for comfort and believe for others more than for yourself." },
];
/** Drafted: what "your theme" is (it is the lowest-scoring of the four). */
export const THEME_INTRO = "Freedom is measured across four themes. Yours is the one you scored lowest: where old beliefs hold you back most.";

/** Drafted for Taylor (Oct 21 call: a taker read the office pattern as a church office). */
export const OFFICE_TIP =
  "This compares how your gifts combine with the five roles named in Ephesians\u00a04:\napostle, prophet, evangelist, shepherd and teacher.\n\nIt describes a pattern in your gifts, not a title or a church office.";
